import { useMemo } from 'react'
import { Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { LABEL_STYLE, labelSheetHtml } from '@/lib/inventoryLabels'
import { printLabelViaIframe } from '@/lib/printLabel'
import type { PrintSubject } from '@/api/labelPrintingApi'
import { PrintLabelDialog } from '@/components/labels/print/PrintLabelDialog'

/**
 * Preview and print inventory labels — one lot, one location, or a batch.
 *
 * Deliberately thin: every decision about what a label SAYS lives in
 * `src/lib/inventoryLabels.ts`, which is a pure module with tests. This repo's
 * vitest config collects only `src/**\/*.test.ts` in a node environment, so
 * anything moved into this file becomes untestable — the same split
 * `ProductLabelPreviewDialog` follows.
 *
 * The preview is the printed markup, not a re-render of it. A preview built
 * separately from the print path is a preview that can lie, and the one thing
 * an operator uses this screen for is to confirm the label is right before
 * committing it to a shelf.
 */
export interface LabelPreviewRequest {
  /** Dialog heading, e.g. "Lot TB-2405" or "12 locations · Zone Z1". */
  title: string
  /** Rendered label HTML, one entry per physical label. */
  labels: string[]
  /**
   * The exact strings the symbols encode, surfaced as text.
   *
   * On the label these sit at print size under a symbol, which is right for
   * paper and wrong for a screen: this dialog is where someone checks the code
   * BEFORE committing it to a shelf, and they cannot check what they cannot
   * read. Same values, larger and selectable, so they can be compared against
   * the pick list or pasted into a scanner test.
   */
  codes: { caption: string; value: string }[]
  /**
   * What these labels are, for the label printers. With it, the print popup
   * opens instead: pick a printer, check, print; this browser label stays as
   * "Print in browser". One id per label, in the order of `labels`.
   */
  print?: { subject: PrintSubject; ids: string[] }
}

export function LabelPreviewDialog({
  request,
  onClose,
}: {
  request: LabelPreviewRequest | null
  onClose: () => void
}) {
  if (request?.print) return <PrinterLabelDialog request={request} print={request.print} onClose={onClose} />
  return <BrowserLabelDialog request={request} onClose={onClose} />
}

/** The print popup, with this dialog's browser label as the fallback. */
function PrinterLabelDialog({
  request,
  print,
  onClose,
}: {
  request: LabelPreviewRequest
  print: NonNullable<LabelPreviewRequest['print']>
  onClose: () => void
}) {
  const printRequest = useMemo(
    () => ({
      subject: print.subject,
      ids: print.ids,
      title: request.title,
      browser: {
        print: () => printLabelViaIframe(request.title, labelSheetHtml(request.labels), LABEL_STYLE),
        previewHtml: request.labels.map(l => labelSheetHtml([l])),
        previewStyle: LABEL_STYLE,
      },
    }),
    [request, print],
  )
  return <PrintLabelDialog request={printRequest} onClose={onClose} />
}

function BrowserLabelDialog({
  request,
  onClose,
}: {
  request: LabelPreviewRequest | null
  onClose: () => void
}) {
  const sheet = request ? labelSheetHtml(request.labels) : ''
  const count = request?.labels.length ?? 0

  function handlePrint() {
    if (!request || count === 0) return
    printLabelViaIframe(request.title, sheet, LABEL_STYLE)
  }

  const codes = request?.codes ?? []

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      {/* Bounded height with a scrolling middle and a pinned footer. Without
          this the codes list grows with the batch — forty labels pushed the
          preview AND the Print button past the bottom of the viewport, so the
          one action the dialog exists for could not be reached. */}
      <DialogContent className='flex max-h-[85vh] flex-col sm:max-w-2xl'>
        <DialogHeader className='shrink-0'>
          <DialogTitle>{request?.title ?? 'Label'}</DialogTitle>
          <DialogDescription>
            {count === 1
              ? 'Check the code matches the shelf before printing.'
              : 'Check each code before printing.'}
          </DialogDescription>
        </DialogHeader>

        {/* The codes come FIRST. They are what gets verified; the label is what
            gets printed. Putting the artwork above them buried the one thing
            this dialog exists to let someone read. */}
        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto'>
        {codes.length > 0 && (
          <div className='overflow-hidden rounded-md border'>
            <p className='border-b bg-muted/50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground'>
              Scans to
            </p>
            <ul className='divide-y'>
              {codes.map((c) => (
                <li key={`${c.caption}-${c.value}`} className='flex items-baseline gap-3 px-3 py-2.5'>
                  <span className='w-20 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'>
                    {c.caption}
                  </span>
                  {/* Selectable and tabular so digits line up and a QA can copy
                      it straight into a scanner test. */}
                  <code className='select-all break-all font-mono text-base font-semibold tabular-nums'>
                    {c.value}
                  </code>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* A muted ground so the label reads as paper sitting on a desk rather
            than as white markup on a white dialog. The scrolling now belongs to
            the body around it, so the artwork is not a second nested scroller. */}
        <div className='rounded-md bg-muted/40 p-4'>
          {count === 0 ? (
            // An empty state is an instruction, not an apology.
            <p className='py-6 text-center text-sm text-muted-foreground'>
              No scannable codes here yet. Set a lot number or a location first.
            </p>
          ) : (
            <div
              className='flex flex-col items-center [&_.lbl]:shadow-sm'
              dangerouslySetInnerHTML={{ __html: sheet }}
            />
          )}
        </div>
        </div>

        <DialogFooter className='shrink-0 border-t pt-3'>
          <Button variant='outline' onClick={onClose}>
            Close
          </Button>
          <Button onClick={handlePrint} disabled={count === 0}>
            <Printer className='mr-2 h-4 w-4' />
            {count === 1 ? 'Print label' : `Print ${count} labels`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
